import { ResizeObserver } from '@juggle/resize-observer'
import _ from 'lodash'
import queryString from 'query-string'
import React, {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCookies } from 'react-cookie'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { CookieNames } from 'src/cookies'
import { ThunkActionWithProperties } from 'src/redux/configureStore'
import { RootState } from 'src/redux/createRootReducer'
import { mapEnumToValues } from 'src/utils/array.utils'
import { minWithUndefined } from 'src/utils/number.utils'

export const useFlyoutDispatch = (): ThunkDispatch<
  RootState,
  void,
  AnyAction
> => {
  const dispatch = useDispatch()
  // Stable identity across renders: consumers list this in effect dep arrays,
  // so a fresh function each render re-fires those effects (unbounded thunk loops).
  return useMemo((): ThunkDispatch<RootState, void, AnyAction> => {
    const flyoutDispatch: ThunkDispatch<RootState, void, AnyAction> = (
      action: AnyAction | ThunkActionWithProperties,
    ) => {
      action.isDispatchedFromBackgroundPage = false
      return dispatch(action)
    }
    return flyoutDispatch
  }, [dispatch])
}

export const useBackgroundDispatch = (): ThunkDispatch<
  RootState,
  void,
  AnyAction
> => {
  const dispatch = useDispatch()
  // Stable identity across renders — see useFlyoutDispatch.
  return useMemo((): ThunkDispatch<RootState, void, AnyAction> => {
    const backgroundDispatch: ThunkDispatch<RootState, void, AnyAction> = (
      action: AnyAction | ThunkActionWithProperties,
    ) => {
      action.isDispatchedFromBackgroundPage = true
      return dispatch(action)
    }
    return backgroundDispatch
  }, [dispatch])
}

interface UseIsScrolledPastReturn {
  isScrolledPast: boolean
}
export const useIsScrolledPast = (limit: number): UseIsScrolledPastReturn => {
  const [isScrolledPast, setIsScrolledPast] = useState(false)
  useEffect((): void => {
    setIsScrolledPast(window.scrollY > limit)
  }, [limit])

  const handleScroll = useCallback(() => {
    if (window.scrollY > limit && !isScrolledPast) {
      setIsScrolledPast(true)
    } else if (window.scrollY <= limit && isScrolledPast) {
      setIsScrolledPast(false)
    }
  }, [limit, isScrolledPast])
  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return (): void => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return {
    isScrolledPast,
  }
}

export const useScrollY = (): number[] => {
  const [scrollY, setScrollY] = useState(0)
  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY)
  }, [])
  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return (): void => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return [scrollY]
}

export function useEventListener<
  KW extends keyof WindowEventMap,
  KH extends keyof HTMLElementEventMap & keyof SVGElementEventMap,
  KM extends keyof MediaQueryListEventMap,
  T extends HTMLElement | SVGAElement | MediaQueryList = HTMLElement,
>(
  eventName: KW | KH | KM,
  handler: (
    event:
      | WindowEventMap[KW]
      | HTMLElementEventMap[KH]
      | SVGElementEventMap[KH]
      | MediaQueryListEventMap[KM]
      | Event,
  ) => void,
  element?: React.RefObject<T>,
  options?: boolean | AddEventListenerOptions,
): void {
  // Create a ref that stores handler
  const savedHandler = useRef(handler)

  useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window = element?.current ?? window

    if (!(targetElement && targetElement.addEventListener)) return

    const listener: typeof handler = (event) => {
      savedHandler.current(event)
    }

    targetElement.addEventListener(eventName, listener, options)

    return (): void => {
      targetElement.removeEventListener(eventName, listener, options)
    }
  }, [eventName, element, options])
}

export const useClickOutside = (
  callback: () => void,
): React.RefObject<HTMLDivElement> => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [callback])

  return ref
}

interface UseDropdownOpenProps {
  closeOnSecondClick?: boolean
  closeOnAnyClick?: boolean
  keepDropdownOpen?: boolean
  startAndKeepOpen?: boolean
}

interface UseDropdownOpenReturn {
  dropdownOpen: boolean
  divRef: React.MutableRefObject<HTMLDivElement | null>
  handleOpenerClick: (e: React.SyntheticEvent) => void
  setDropdownOpen: (val: boolean) => void
}

export const useDropdownOpen = ({
  closeOnSecondClick,
  closeOnAnyClick,
  keepDropdownOpen,
  startAndKeepOpen: startOpen,
}: UseDropdownOpenProps): UseDropdownOpenReturn => {
  const divRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const [dropdownOpen, setDropdownOpen] = useState(!!startOpen)

  const handleAnyClick = useCallback(
    (e: Event): void => {
      if (
        dropdownOpen &&
        !keepDropdownOpen &&
        !startOpen &&
        ((divRef.current !== null &&
          !divRef.current.contains(e.target as Node)) ||
          closeOnAnyClick)
      ) {
        setDropdownOpen(false)
      }
    },
    [dropdownOpen, keepDropdownOpen, closeOnAnyClick, divRef.current],
  )

  useEventListener('click', handleAnyClick)

  const handleOpenerClick = (e: React.SyntheticEvent): void => {
    e.stopPropagation()
    e.preventDefault()
    if (!dropdownOpen) {
      setDropdownOpen(true)
    } else {
      if ('key' in e.nativeEvent) {
        setDropdownOpen(false)
      } else if (closeOnSecondClick) {
        setDropdownOpen(false)
      }
    }
  }
  return { dropdownOpen, divRef, handleOpenerClick, setDropdownOpen }
}

interface UseSelectedRefAutoScrollProps {
  selectedOptionRef: React.MutableRefObject<HTMLDivElement | null>
  // hideDropdown: boolean
}
export const useSelectedRefAutoScroll = (
  dropdownOpen: boolean,
): UseSelectedRefAutoScrollProps => {
  const selectedOptionRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef(null)

  useEffect((): void => {
    if (selectedOptionRef.current !== null) {
      selectedOptionRef.current.scrollIntoView({
        block: 'center',
        inline: 'start',
      })
    }
  }, [dropdownOpen])

  return {
    selectedOptionRef,
  }
}

interface CircleCoordinates {
  x: number
  y: number
}

interface UseCursorExpandingCircleReturn<T> {
  animationModifiers: Record<string, boolean>
  buttonRef: React.MutableRefObject<T | null>
  circleCoordinates: CircleCoordinates
  handleClick: (e: React.MouseEvent<T>) => void
}

export const useCursorExpandingCircle = <T extends HTMLElement>(
  radius: number,
  onClick?: (e: React.MouseEvent<T>) => void,
): UseCursorExpandingCircleReturn<T> => {
  const buttonRef: React.MutableRefObject<T | null> = useRef(null)
  const [circleCoordinates, setCircleCoordinates] = useState<CircleCoordinates>(
    { x: 0, y: 0 },
  )
  const { animationModifiers, triggerAnimation } = useSingleAnimationState()

  const handleClick = (e: React.MouseEvent<T>): void => {
    if (buttonRef.current !== null) {
      const boundingClientRect = buttonRef.current?.getBoundingClientRect()
      setCircleCoordinates({
        x: e.clientX - boundingClientRect.left - radius,
        y: e.clientY - boundingClientRect.top - radius,
      })
      triggerAnimation()
    }
    if (onClick !== undefined) {
      onClick(e)
    }
  }

  return {
    animationModifiers,
    buttonRef,
    circleCoordinates,
    handleClick,
  }
}

interface UseSingleAnimationStateReturn {
  animationModifiers: Record<string, boolean>
  triggerAnimation: () => void
}

export const useSingleAnimationState = (): UseSingleAnimationStateReturn => {
  const [isAnimated, setIsAnimated] = useState<boolean>(false)
  const [retriggerAnimation, setRetriggerAnimation] = useState(false)

  const triggerAnimation = (): void => {
    setIsAnimated(false)
    setRetriggerAnimation(true)
  }

  useEffect((): void => {
    // uses a retrigger flag and temporarily flip to false to know to reinitiate
    if (isAnimated === false && retriggerAnimation === true) {
      setIsAnimated(true)
      setRetriggerAnimation(false)
    }
  }, [isAnimated, retriggerAnimation])

  const animationModifiers = {
    isAnimated: isAnimated === true,
  }

  return {
    animationModifiers,
    triggerAnimation,
  }
}

interface UseDualAnimationStateReturn {
  onAnimationEnd: () => void
  booleanState: boolean
  animationModifiers: Record<string, boolean>
  triggerFirstToSecond: () => void
  triggerSecondToFirst: () => void
  toggleAnimation: () => void
}

export const useDualAnimationState = (
  initialBooleanState?: boolean,
): UseDualAnimationStateReturn => {
  const [animationState, setAnimationState] = useState(
    initialBooleanState === true ? 2 : 0,
  )
  const onAnimationEnd = (): void => {
    if (animationState % 2 === 1) {
      setAnimationState((animationState + 1) % 4)
    }
  }

  const booleanState = animationState === 1 || animationState === 2 // first state has to be false, second true

  const animationModifiers = {
    first: !booleanState,
    second: booleanState,
    firstToSecond: animationState === 1,
    secondToFirst: animationState === 3,
  }

  const triggerFirstToSecond = (): void => {
    setAnimationState(1)
  }

  const triggerSecondToFirst = (): void => {
    setAnimationState(3)
  }

  const toggleAnimation = (): void => {
    if (booleanState) {
      triggerSecondToFirst()
    } else {
      triggerFirstToSecond()
    }
  }

  return {
    onAnimationEnd,
    booleanState,
    animationModifiers,
    triggerFirstToSecond,
    triggerSecondToFirst,
    toggleAnimation,
  }
}

interface UseCookieStateReturn<R> {
  cookieValue: R
  updateCookieValue: (r: R) => void
}
export const useCookieState = <R>(
  cookieName: CookieNames,
  valuePath: string,
  defaultValue: R,
): UseCookieStateReturn<R> => {
  const [cookies, setCookie] = useCookies([cookieName])
  const cookieValue =
    cookies[cookieName] !== undefined &&
    cookies[cookieName][valuePath] !== undefined
      ? cookies[cookieName][valuePath]
      : defaultValue
  const updateCookieValue = (nextVal: R): void => {
    setCookie(
      cookieName,
      {
        ...(cookies[cookieName] || {}),
        [valuePath]: nextVal,
      },
      {
        path: '/',
        expires: new Date('January 1, 3000 00:00:00'),
      },
    )
  }

  return {
    cookieValue,
    updateCookieValue,
  }
}

interface UseFormFieldsReturn<T> {
  fields: T
  setFields: (t: T) => void
  fieldsErrors: Partial<Record<keyof T, Array<React.ReactElement | string>>>
  setFieldsErrors: (
    error: Partial<Record<keyof T, Array<React.ReactElement | string>>>,
  ) => void
  topmostScrollGroup?: number
  setTopmostScrollGroup: (val: number | undefined) => void
  onFieldChange: (fieldName: keyof T, value: T[keyof T]) => void
  onMultiFieldChange: (
    fieldNames: Array<keyof T>,
    values: Array<T[keyof T]>,
  ) => void
  setErrorsAndReturnDidPassValidation: () => boolean
  hasFieldsErrors: boolean
}

export type FormFieldsValidation<T> = Partial<{
  [field in keyof T]: Array<(val: T[field]) => React.ReactElement | undefined>
}>

export type FormFieldsBackendValidation<T> = {
  errors?: Record<string, Array<React.ReactElement | string>>
  keyMap?: Record<string, keyof T>
}

export const useFormFields = <T extends Record<string, unknown>>(
  initialFormFields: T,
  fieldsValidation: (fields: T) => Partial<{
    [field in keyof T]: Array<(val: T[field]) => React.ReactElement | undefined>
  }>,
  backendErrors?: Record<string, Array<React.ReactElement | string>>,
  backendFieldNameMap?: Record<string, keyof T>,
  scrollGroups?: Partial<Record<keyof T, number>> | number,
): UseFormFieldsReturn<T> => {
  const [fields, setFields] = useState(initialFormFields)
  const [fieldsErrors, setFieldsErrors] = useState<
    Partial<Record<keyof T, Array<React.ReactElement | string>>>
  >({})
  const [topmostScrollGroup, setTopmostScrollGroup] = useState<
    number | undefined
  >()

  const hasFieldsErrors = useMemo(
    () =>
      Object.values(fieldsErrors).some(
        (errors) => errors !== undefined && errors.length > 0,
      ),
    [fieldsErrors],
  )

  const constructedValidation = fieldsValidation(fields)

  const onFieldChange = (fieldName: keyof T, value: T[keyof T]): void => {
    setFields({
      ...fields,
      [fieldName]: value,
    })
  }

  const onMultiFieldChange = (
    fieldNames: Array<keyof T>,
    values: Array<T[keyof T]>,
  ): void => {
    setFields({
      ...fields,
      ...fieldNames.reduce(
        (acc, curr, i) => ({
          ...acc,
          [curr]: values[i],
        }),
        {},
      ),
    })
  }

  const setErrorsAndReturnDidPassValidation = (
    backendErrors?: Record<string, Array<React.ReactElement | string>>,
  ): boolean => {
    // side effect of setting errors
    const validation: Partial<
      Record<keyof T, Array<React.ReactElement | string>>
    > =
      backendErrors !== undefined
        ? backendFieldNameMap === undefined
          ? (backendErrors as Record<
              keyof T,
              Array<React.ReactElement | string>
            >) // asserts that if there's no map, keys are keys
          : Object.keys(backendErrors).reduce(
              (
                acc: Partial<
                  Record<keyof T, Array<React.ReactElement | string>>
                >,
                curr: string,
              ) =>
                backendFieldNameMap !== undefined &&
                backendFieldNameMap[curr] !== undefined
                  ? {
                      ...acc,
                      [backendFieldNameMap[curr]]: backendErrors[curr],
                    }
                  : acc,
              {},
            )
        : Object.keys(constructedValidation).reduce((acc, curr) => {
            const validated =
              constructedValidation !== undefined &&
              constructedValidation[curr] !== undefined
                ? constructedValidation?.[curr]
                    ?.map((validator) =>
                      validator(fields[curr] as T[typeof curr]),
                    )
                    .filter((val) => val !== undefined)
                : undefined
            return validated?.length
              ? {
                  ...acc,
                  [curr]: validated,
                }
              : acc
          }, {})

    const topmostScrollGroupLocal = minWithUndefined(
      ...Object.keys(validation).map((key) => {
        if (typeof scrollGroups === 'number') {
          return scrollGroups
        } else {
          return scrollGroups?.[key]
        }
      }),
    )

    setFieldsErrors(validation)
    setTopmostScrollGroup(topmostScrollGroupLocal)
    return Object.keys(validation).length === 0
  }

  useEffect((): void => {
    if (backendErrors !== undefined) {
      setErrorsAndReturnDidPassValidation(backendErrors)
    }
  }, [backendErrors])

  return {
    fields,
    setFields,
    fieldsErrors,
    setFieldsErrors,
    topmostScrollGroup,
    setTopmostScrollGroup,
    onFieldChange,
    onMultiFieldChange,
    setErrorsAndReturnDidPassValidation,
    hasFieldsErrors,
  }
}

export const useScrollToScrollGroupRefs = (
  refs: Array<React.MutableRefObject<HTMLElement | null>>,
  topmostScrollGroups: Array<number | undefined>,
  setTopmostScrollGroups?: Array<(value: number | undefined) => void>,
): void => {
  useEffect((): void => {
    const min = minWithUndefined(...topmostScrollGroups)
    if (min !== undefined) {
      refs[min]?.current?.scrollIntoView()
    }
    setTopmostScrollGroups?.forEach((setFn) => {
      setFn(undefined)
    })
  }, topmostScrollGroups)
}

export const useKeyedDivRefs = <T extends Record<string, string | number>>(
  enumObj: T,
): Record<T[keyof T], React.RefObject<HTMLDivElement>> | undefined => {
  const enumLength = useMemo(() => Object.keys(enumObj).length / 2, [enumObj])
  return useMemo(
    () =>
      mapEnumToValues(
        enumObj,
        Array(enumLength)
          .fill(null)
          .map(() => React.createRef<HTMLDivElement>()),
      ),
    [enumLength, enumObj],
  )
}

interface UseStringSetStateReturn {
  set: Set<string>
  setSet: (set: Set<string>) => void
  handleSelection: (val: string) => Set<string>
}

export const useStringSetState = (
  initialValues?: Set<string>,
): UseStringSetStateReturn => {
  const [set, setSet] = useState(initialValues || new Set<string>())

  /* Updates the state set and returns the new value for potential immediate use */
  const handleSelection = (val: string): Set<string> => {
    const newSet = new Set(set)
    if (newSet.has(val)) {
      newSet.delete(val)
    } else {
      newSet.add(val)
    }
    setSet(newSet)
    return newSet
  }

  return {
    set,
    setSet,
    handleSelection,
  }
}

export const useChangedEffect = (
  effect: React.EffectCallback,
  deps?: React.DependencyList,
): void => {
  const isFirstRender = useRef(true)
  useEffect((): void => {
    if (isFirstRender.current === true) {
      isFirstRender.current = false
    } else {
      effect()
    }
  }, deps)
}

export const useWindowScrollBottomHandler = (
  handleScroll: () => void,
  distanceFromBottomToRefetch = 100,
): void => {
  const handleScrollThrottled = _.throttle(handleScroll, 500)
  useEventListener('scroll', (): void => {
    // assumes this will always be at the bottom of the page... which is fair
    const heightTotal = document.body.scrollHeight
    const scrolledTotal =
      window.innerHeight + (window.scrollY || window.pageYOffset)
    const distanceFromBottom = heightTotal - scrolledTotal
    if (distanceFromBottom < distanceFromBottomToRefetch) {
      handleScrollThrottled()
    }
  })

  const isFirstFetch = useRef(true)
  const [resizeRefetchTriggered, setResizeRefetchTriggered] = useState(true)
  const [resizeObserver] = useState(() => {
    const observer = new ResizeObserver(
      _.debounce(() => {
        setResizeRefetchTriggered(true)
      }, 50),
    )
    return observer
  })
  useEffect((): (() => void) => {
    resizeObserver.observe(document.body)
    return (): void => {
      resizeObserver.disconnect()
    }
  }, [])
  useEffect((): void => {
    setResizeRefetchTriggered(false)
    if (
      isFirstFetch.current === true ||
      document.body.scrollHeight <=
        window.innerHeight + distanceFromBottomToRefetch
    ) {
      isFirstFetch.current = false
      handleScroll()
    }
  }, [handleScroll, resizeRefetchTriggered])
}

interface UseElementScrollBottomHandlerReturn {
  onScroll: (e: React.UIEvent<HTMLElement>) => void
  childRef: (node: HTMLDivElement) => void
  parentRef: (node: HTMLDivElement) => void
}

export const useElementScrollBottomHandler = (
  handleScroll: () => void,
  distanceFromBottomToRefetch = 100,
): UseElementScrollBottomHandlerReturn => {
  const onScroll = (e: React.UIEvent<HTMLElement>): void => {
    // assumes this will always be at the bottom of the page... which is fair
    const heightTotal = e.currentTarget.scrollHeight
    const scrolledTotal =
      e.currentTarget.scrollTop + e.currentTarget.clientHeight
    const distanceFromBottom = heightTotal - scrolledTotal
    if (distanceFromBottom < distanceFromBottomToRefetch) {
      handleScroll()
    }
  }

  const childElement = useRef<HTMLDivElement | undefined>()
  const parentElement = useRef<HTMLDivElement | undefined>()
  const isFirstFetch = useRef(true)
  const [resizeRefetchTriggered, setResizeRefetchTriggered] = useState(true)
  const [resizeObserver] = useState(() => {
    const observer = new ResizeObserver(
      _.debounce(() => {
        setResizeRefetchTriggered(true)
      }, 50),
    )
    return observer
  })
  const childRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      childElement.current = node
      resizeObserver.observe(node)
    } else {
      if (childElement.current) {
        resizeObserver.unobserve(childElement.current)
      }
      childElement.current = undefined
    }
  }, [])
  const parentRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      parentElement.current = node
      resizeObserver.observe(node)
    } else {
      if (parentElement.current) {
        resizeObserver.unobserve(parentElement.current)
      }
      parentElement.current = undefined
    }
  }, [])
  useEffect((): void => {
    setResizeRefetchTriggered(false)
    if (
      isFirstFetch.current === true ||
      (childElement.current !== undefined &&
        parentElement.current !== undefined &&
        childElement.current.clientHeight <=
          parentElement.current.clientHeight + distanceFromBottomToRefetch)
    ) {
      isFirstFetch.current = false
      handleScroll()
    }
  }, [handleScroll, resizeRefetchTriggered])
  return {
    onScroll,
    childRef,
    parentRef,
  }
}

interface UseSortableReturn {
  setSort: (field: string) => void
  sortBy?: string
  sortDescending?: boolean
}

export const useSortable = (
  initialSortBy?: string,
  initialSortDescending?: boolean,
): UseSortableReturn => {
  const [sortBy, setSortBy] = useState<string | undefined>(initialSortBy)
  const [sortDescending, setSortDescending] = useState<boolean | undefined>(
    initialSortDescending,
  )

  const setSort = useCallback(
    (field: string): void => {
      if (sortBy === field) {
        if (sortDescending === false) {
          setSortDescending(true)
        } else {
          setSortBy(initialSortBy)
          setSortDescending(initialSortDescending)
        }
      } else {
        setSortBy(field)
        setSortDescending(false)
      }
    },
    [sortBy, sortDescending],
  )

  return {
    setSort,
    sortBy,
    sortDescending,
  }
}

interface UseDebouncedReturn<T extends unknown[]> {
  debouncedF: (...args: T) => void
  cancel: () => void
}

export const useDebounced = <T extends unknown[]>(
  f: (...args: T) => void,
  delay = 500,
  isLeading = false,
): UseDebouncedReturn<T> => {
  const delayedF = useRef<NodeJS.Timeout | undefined>()
  const cancel = (): void => {
    if (delayedF.current !== undefined) {
      clearTimeout(delayedF.current)
      delayedF.current = undefined
    }
  }
  const debouncedF = (...args: T): void => {
    if (delayedF.current !== undefined) {
      clearTimeout(delayedF.current)
    } else {
      if (isLeading) {
        // leading edge: call the debounced function at the start of the timer
        f(...args)
      }
    }
    delayedF.current = setTimeout(() => {
      if (!isLeading) {
        // trailing edge: call the debounced function at the end of the timer
        f(...args)
      }
      delayedF.current = undefined
    }, delay)
  }
  return {
    debouncedF,
    cancel,
  }
}

export const useDebounceValue = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return (): void => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

interface UseSlidingNavBarReturn {
  barStyle: React.CSSProperties | undefined
  showStaticBars: boolean
  containerDivRef: (node: HTMLDivElement | null) => void
}
export const useSlidingNavBar = (
  selectedIndex: number | undefined,
  barStyleFromElement: (el: HTMLElement) => React.CSSProperties,
): UseSlidingNavBarReturn => {
  const [barStyle, setBarStyle] = useState<React.CSSProperties | undefined>()
  const [showStaticBars, setShowStaticBars] = useState(true)
  const [triggerReposition, setTriggerReposition] = useState(false)

  useEffect((): (() => void) => {
    const resizeListener = (): void => {
      setTriggerReposition(true)
    }
    window.addEventListener('resize', resizeListener)
    return (): void => {
      window.removeEventListener('resize', resizeListener)
    }
  }, [])

  const containerDivRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (node && selectedIndex !== undefined) {
        const a = node.childNodes[selectedIndex]
        if (a instanceof HTMLElement) {
          setShowStaticBars(false)
          setBarStyle(barStyleFromElement(a))
          setTriggerReposition(false)
        }
      } else {
        setShowStaticBars(true)
      }

      // TODO: Ryan T to eventually rework
      // if (node !== null && (node.scrollHeight > node.clientHeight)) {
      //   setShowStaticBars(true)
      // }
    },
    [selectedIndex, triggerReposition],
  )

  return {
    barStyle,
    showStaticBars,
    containerDivRef,
  }
}

export const useWindowSize = (): number[] => {
  const [size, setSize] = useState([0, 0])
  useLayoutEffect(() => {
    function updateSize(): void {
      setSize([window.innerWidth, window.innerHeight])
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return (): void => window.removeEventListener('resize', updateSize)
  }, [])
  return size
}

export const useInterval = (
  callback: () => void,
  delay: number | null,
): void => {
  const savedCallback = useRef(callback)

  // Remember the latest callback if it changes.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return
    }

    const id = setInterval(() => savedCallback.current(), delay)

    return (): void => clearInterval(id)
  }, [delay])
}

// Returns an array if given a string or array value or returns undefined
// TODO: move and rename this, it's not a hook
export const useQueryParamArrayHandler = (
  param: any,
): any[] | any | undefined => {
  const paramType = typeof param

  switch (paramType) {
    case 'string':
      return [param]
    case 'object':
      return param
    default:
      return undefined
  }
}

// utility hook for tracking previous state
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

export function useHover<T extends HTMLElement = HTMLElement>(
  delay?: number,
): { hovered: boolean; hoveredRef: RefObject<T> } {
  const hoveredRef = useRef<T>(null)
  const [hoveredInternal, setHoveredInternal] = useState<boolean>(false)
  const [hovered, setHovered] = useState<boolean>(false)

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    if (hoveredInternal) {
      if (delay !== undefined) {
        timer = setTimeout(() => setHovered(true), delay)
      } else {
        setHovered(true)
      }
    } else {
      setHovered(false)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [hoveredInternal])

  const handleMouseEnter = (): void => {
    setHoveredInternal(true)
  }
  const handleMouseLeave = (): void => {
    setHoveredInternal(false)
  }

  useEventListener('mouseenter', handleMouseEnter, hoveredRef)
  useEventListener('mouseleave', handleMouseLeave, hoveredRef)

  return {
    hovered,
    hoveredRef,
  }
}

export function useTypingEffect(
  fullText: string,
  speed = 30,
): [string, boolean] {
  const [typedText, setTypedText] = useState('')
  const [isDoneTyping, setIsDoneTyping] = useState(false)

  useEffect(() => {
    setIsDoneTyping(false)
    const interval = setInterval(() => {
      if (typedText === fullText) {
        setIsDoneTyping(true)
        return
      }

      let nextText = typedText

      if (
        typedText.length > fullText.length ||
        !fullText.startsWith(typedText)
      ) {
        // Backspace
        nextText = typedText.slice(0, -1)
      } else {
        // Type next character
        nextText = fullText.slice(0, typedText.length + 1)
      }

      setTypedText(nextText)
    }, speed)

    return () => clearInterval(interval)
  }, [fullText, typedText, speed])

  return [typedText, isDoneTyping]
}

export function useLoopingTypingEffect(
  fullTexts: string[],
  typingSpeed = 30,
  pauseDuration = 3000,
  deleteSpeed = 15,
): string {
  const [currentFullText, setCurrentFullText] = useState(fullTexts[0])
  const [index, setIndex] = useState<number>(0)
  const [speed, setSpeed] = useState<number>(typingSpeed)

  const pickNextIndex = (): number => {
    let newIndex = index
    while (newIndex === index) {
      newIndex = Math.floor(Math.random() * fullTexts.length)
    }
    return newIndex
  }

  const [text, isDoneTyping] = useTypingEffect(currentFullText, speed)

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (isDoneTyping && currentFullText) {
      timeout = setTimeout(() => {
        setCurrentFullText('')
        setSpeed(deleteSpeed)
      }, pauseDuration)
    } else if (isDoneTyping) {
      const nextIndex = pickNextIndex()
      setCurrentFullText(fullTexts[nextIndex])
      setSpeed(typingSpeed)
      setIndex(nextIndex)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [isDoneTyping])

  return text
}

export function useQueryParams() {
  const { search } = useLocation()
  return useMemo(() => queryString.parse(search), [search])
}
